import { format, getHours, getMinutes, setHours, setMinutes } from 'date-fns'
import debounce from 'lodash/debounce'
import { bool, func, string, oneOf, instanceOf } from 'prop-types'
import React, { PureComponent } from 'react'
import { AnalyticsLayer } from '@opallabs/quanta'
import DatePickerPopoverV2 from '../../Inputs/DatePicker/PopoverV2'
import { kuiperClassNames } from '../../utils'
import Cell from '../Cell'

import './styles.scss'

const DATE_FORMAT = 'MMM d'
const POPOVER_DELAY = 250
class DatePickerCell extends PureComponent {
  static propTypes = {
    active: bool,
    endDate: instanceOf(Date),
    entityId: string,
    entityType: string,
    focusCell: func,
    focused: bool,
    getCellContents: func.isRequired,
    onChange: func,
    onKeyDown: func,
    onPopoverKeydown: func,
    registerPopover: func.isRequired,
    startDate: instanceOf(Date),
    startOfWeek: oneOf([0, 1, 2, 3, 4, 5, 6]),
    tabbable: bool,
    title: string,
    unregisterPopover: func.isRequired,
  }

  static defaultProps = {
    startOfWeek: 1,
    focusCell: () => {},
  }

  constructor(props) {
    super(props)

    const cellContents = this.props.getCellContents(this.props)
    const endDate = cellContents ? new Date(cellContents) : null
    this.state = {
      endDate,
    }

    this.debouncedOnChange = debounce(this.props.onChange, POPOVER_DELAY)
    this.popoverRef = React.createRef()
  }

  componentDidUpdate = (prevProps) => {
    const focusedChanged = prevProps.focused !== this.props.focused

    if (focusedChanged) {
      if (this.popoverIsOpen) {
        this.props.registerPopover(this.popoverRef)
      } else {
        this.props.unregisterPopover()
      }
    } else if (prevProps.endDate !== this.props.endDate) {
      const endDate = new Date(this.props.endDate)
      this.setState({
        endDate,
      })
    }
  }

  get popoverIsOpen() {
    return this.props.active && this.props.focused
  }

  updateEndDateFromCalendar = (date) => {
    this.setState(
      (state) => {
        let dateWithTime = date
        if (state.endDate !== null) {
          const prevHours = getHours(state.endDate)
          const prevMinutes = getMinutes(state.endDate)
          dateWithTime = setHours(setMinutes(date, prevMinutes), prevHours)
        }

        return {
          endDate: dateWithTime,
          popoverIsOpen: false,
        }
      },
      () => {
        this.debouncedOnChange(
          this.props.entityType,
          this.props.entityId,
          this.state.endDate
        )
        this.props.focusCell()
      }
    )
  }

  get cellContents() {
    const startDate = new Date(this.props.startDate)

    return (
      <AnalyticsLayer name={this.props.title}>
        <DatePickerPopoverV2
          ariaLabel={this.props.title}
          allowableDates={{ start: startDate }}
          classNames="end-date-picker"
          classNameObj={{ tracking: 'end-date-picker' }}
          date={this.state.endDate}
          dateFormat={DATE_FORMAT}
          displayType="input"
          focusable={this.props.tabbable}
          onSelect={this.updateEndDateFromCalendar}
          propOverrideMode={true}
          popoverIsClosed={!this.popoverIsOpen}
          popoverRef={this.popoverRef}
          popoverOptions={{
            position: 'bottom',
            triggerEvent: 'manual',
            popoverHandlers: {
              open: Boolean(this.popoverIsOpen),
              hideOnClick: true,
              flip: true,
              flipBehavior: ['bottom', 'top', 'right', 'left'],
              // Fixes global styling bleedover from spreadsheet
              appendTo: () => document.body,
            },
          }}
          popoverKeyDown={this.props.onPopoverKeydown}
          startOfWeek={this.props.startOfWeek}
          text={
            this.state.endDate ? format(this.state.endDate, DATE_FORMAT) : '—'
          }
          title="Select an end date"
        />
      </AnalyticsLayer>
    )
  }

  render() {
    return (
      <Cell
        {...this.props}
        className={kuiperClassNames('date-picker-cell')}
        onKeyDown={this.props.onKeyDown}
      >
        {this.cellContents}
      </Cell>
    )
  }
}

export default DatePickerCell
